import { useState } from 'react'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { FileSpreadsheet } from 'lucide-react'
import * as XLSX from 'xlsx'
import { api } from '@/lib/api'
import { daysAgoInMalaysia } from '@/lib/date'
import type { IssueReportSummary, OperatorReportRow, OverviewReport, SalesReport, SlaRow, ThroughputReportRow } from '@/types'
import Card from '@/components/ui/Card'
import PageHeader from '@/components/ui/PageHeader'
import Button from '@/components/ui/Button'
import LogoLoader from '@/components/ui/LogoLoader'
import { cn } from '@/lib/cn'
import { buildReportWorkbook, type ReportTab } from '@/pages/admin/reports/excel'
import OverviewTab from '@/pages/admin/reports/OverviewTab'
import OperatorsTab from '@/pages/admin/reports/OperatorsTab'
import ThroughputTab from '@/pages/admin/reports/ThroughputTab'
import IssuesTab from '@/pages/admin/reports/IssuesTab'
import SalesTab from '@/pages/admin/reports/SalesTab'
import DateRangeBar from '@/components/report/DateRangeBar'

const TABS: { key: ReportTab; label: string }[] = [
  { key: 'overview', label: 'Overview' },
  { key: 'operators', label: 'Operators' },
  { key: 'throughput', label: 'Throughput' },
  { key: 'issues', label: 'Issues' },
  { key: 'sales', label: 'Sales' },
]

const pill = (active: boolean) =>
  cn(
    'rounded-lg border px-3.5 py-2 text-sm font-semibold',
    active ? 'border-primary/60 bg-primary/20 text-primary' : 'border-line text-muted hover:bg-foreground/5',
  )

/**
 * Production, issue and sales reports over one shared date range. Each tab
 * has its own query, fetched only while it is open; the stage-log tabs read
 * the same QR scan logs the SLA report does, sales reads settled orders.
 */
export default function AdminReports() {
  const [tab, setTab] = useState<ReportTab>('overview')
  const [from, setFrom] = useState(daysAgoInMalaysia(29))
  const [to, setTo] = useState(daysAgoInMalaysia(0))

  const range = { from, to }

  // One query per tab, fetched only while that tab is open. keepPreviousData
  // leaves the last chart on screen while a new range loads.
  const overview = useQuery({
    queryKey: ['report-overview', range],
    queryFn: async () => (await api.get<{ data: OverviewReport }>('/admin/reports/overview', { params: range })).data.data,
    enabled: tab === 'overview',
    placeholderData: keepPreviousData,
  })
  const operators = useQuery({
    queryKey: ['report-operators', range],
    queryFn: async () => (await api.get<{ data: OperatorReportRow[] }>('/admin/reports/operators', { params: range })).data.data,
    enabled: tab === 'operators',
    placeholderData: keepPreviousData,
  })
  const throughput = useQuery({
    queryKey: ['report-throughput', range],
    queryFn: async () => (await api.get<{ data: ThroughputReportRow[] }>('/admin/reports/throughput', { params: range })).data.data,
    enabled: tab === 'throughput',
    placeholderData: keepPreviousData,
  })
  // Same key as the Settings > SLA page, so the two share one cache entry.
  const sla = useQuery({
    queryKey: ['sla-report'],
    queryFn: async () => (await api.get<{ data: SlaRow[] }>('/admin/reports/sla')).data.data,
    enabled: tab === 'throughput',
  })
  const issues = useQuery({
    queryKey: ['report-issues', range],
    queryFn: async () => (await api.get<{ data: IssueReportSummary }>('/admin/reports/issues', { params: range })).data.data,
    enabled: tab === 'issues',
    placeholderData: keepPreviousData,
  })
  const sales = useQuery({
    queryKey: ['report-sales', range],
    queryFn: async () => (await api.get<{ data: SalesReport }>('/admin/reports/sales', { params: range })).data.data,
    enabled: tab === 'sales',
    placeholderData: keepPreviousData,
  })

  const active = { overview, operators, throughput, issues, sales }[tab]

  const exportExcel = () => {
    const book = buildReportWorkbook(tab, range, {
      overview: overview.data,
      operators: operators.data,
      throughput: throughput.data,
      sla: sla.data,
      issues: issues.data,
      sales: sales.data,
    })
    if (book) XLSX.writeFile(book, `${tab}-report-${from}-to-${to}.xlsx`)
  }

  const applyPreset = (preset: { from: string; to: string }) => {
    setFrom(preset.from)
    setTo(preset.to)
  }

  return (
    <div>
      <PageHeader
        title="Reports"
        subtitle="Production output, flagged issues and sales over a date range"
        actions={
          <Button variant="outline" onClick={exportExcel} disabled={!active.data}>
            <FileSpreadsheet className="size-4" /> Export Excel
          </Button>
        }
      />

      <Card className="mb-5">
        <div className="flex flex-wrap gap-1.5">
          {TABS.map((t) => (
            <button key={t.key} type="button" onClick={() => setTab(t.key)} className={pill(tab === t.key)}>
              {t.label}
            </button>
          ))}
        </div>

        <div className="mt-4">
          <DateRangeBar from={from} to={to} onFrom={setFrom} onTo={setTo} onPreset={applyPreset} />
        </div>
      </Card>

      {active.isLoading ? (
        <LogoLoader />
      ) : (
        <div className={active.isFetching ? 'opacity-60 transition-opacity' : 'transition-opacity'}>
          {tab === 'overview' && overview.data && <OverviewTab data={overview.data} range={range} />}
          {tab === 'operators' && operators.data && <OperatorsTab data={operators.data} range={range} />}
          {tab === 'throughput' && throughput.data && <ThroughputTab data={throughput.data} sla={sla.data} range={range} />}
          {tab === 'issues' && issues.data && <IssuesTab data={issues.data} range={range} />}
          {tab === 'sales' && sales.data && <SalesTab data={sales.data} />}
          {active.isError && (
            <Card className="text-center text-sm text-status-danger">Could not load this report. Try a shorter date range.</Card>
          )}
        </div>
      )}
    </div>
  )
}
